/* Olivia Carr — header behaviour.
   Vanilla, deferred, no dependencies. Handles the mobile overlay (focus trap,
   scroll lock, Esc, focus return) and the desktop dropdown. */

(function () {
  'use strict';

  var header = document.querySelector('[data-header]');
  if (!header) return;

  var toggle = header.querySelector('[data-nav-toggle]');
  var nav = header.querySelector('#hdr-nav');
  var triggers = Array.prototype.slice.call(
    header.querySelectorAll('[data-dropdown-trigger]')
  );

  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea';
  /* Matches sections/header.liquid's @media breakpoint — keep both in sync. */
  var DESKTOP = window.matchMedia('(min-width: 1100px)');

  /* The overlay starts below the header bar, so its height has to be a real
     measurement rather than a guess. */
  function measure() {
    header.style.setProperty('--header-height', header.offsetHeight + 'px');
  }

  measure();
  window.addEventListener('resize', measure);

  /* ---------------- Dropdowns ---------------- */

  function closeDropdown(trigger) {
    trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAllDropdowns(except) {
    triggers.forEach(function (t) {
      if (t !== except) closeDropdown(t);
    });
  }

  /* Mobile has no accordion — the dropdown's children are permanently
     visible there (see .hdr__submenu in the stylesheet), so the toggle
     behaviour is desktop-only. Guarded here rather than removing the
     listener, since DESKTOP can flip live on rotation/resize. */
  triggers.forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      if (!DESKTOP.matches) return;
      var open = trigger.getAttribute('aria-expanded') === 'true';
      closeAllDropdowns(trigger);
      trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  });

  document.addEventListener('click', function (event) {
    if (!header.contains(event.target)) closeAllDropdowns();
  });

  /* ---------------- Mobile overlay ---------------- */

  var isOpen = false;
  var scrollY = 0;

  function lockScroll() {
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.insetInline = '0';
  }

  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.insetInline = '';

    /* While the body is position:fixed the document collapses to viewport
       height, so its maximum scroll is 0. Reading a layout property forces the
       reflow that restores the real height — without it the scrollTo below is
       clamped to 0 and closing the menu throws the reader back to the top. */
    void document.body.offsetHeight;

    window.scrollTo(0, scrollY);
  }

  function openNav() {
    if (!toggle || isOpen) return;
    isOpen = true;
    /* Root cause of the real-device bleed-through: the hide-on-scroll
       behaviour below only clears data-hidden reactively, on the next
       scroll event. If the header was mid-hide (translate: 0 -100%) at the
       exact moment the toggle was tapped — a real sequence on a phone,
       tap arriving before the next scroll frame — data-nav-open and
       data-hidden were briefly both present. translate on the header
       becomes the containing block for the drawer's position: fixed (it's
       a descendant), so the drawer inherited that offset instead of
       positioning against the real viewport, opening a gap at the top
       where page content showed through. Clearing it unconditionally here
       guarantees the header — and the drawer nested inside it — is never
       in a translated state while the overlay is open. */
    header.removeAttribute('data-hidden');
    header.setAttribute('data-nav-open', '');
    toggle.setAttribute('aria-expanded', 'true');
    lockScroll();

    var first = nav && nav.querySelector(FOCUSABLE);
    if (first) first.focus({ preventScroll: true });
  }

  function closeNav(returnFocus) {
    if (!toggle || !isOpen) return;
    isOpen = false;
    header.removeAttribute('data-nav-open');
    toggle.setAttribute('aria-expanded', 'false');
    closeAllDropdowns();

    /* Focus first, unlock second. Returning focus to the toggle scrolls it into
       view, and on a page that was scrolled before the menu opened that means
       jumping to the top. preventScroll handles it where supported; restoring
       the scroll position afterwards handles everywhere else. */
    if (returnFocus) toggle.focus({ preventScroll: true });

    unlockScroll();
  }

  if (toggle) {
    toggle.addEventListener('click', function () {
      if (isOpen) {
        closeNav(true);
      } else {
        openNav();
      }
    });
  }

  /* Esc closes the innermost thing that is open, and returns focus to whatever
     opened it — never dumps the user at the top of the document. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var openTrigger = triggers.filter(function (t) {
      return t.getAttribute('aria-expanded') === 'true';
    })[0];

    if (openTrigger) {
      closeDropdown(openTrigger);
      openTrigger.focus();
      return;
    }

    if (isOpen) closeNav(true);
  });

  /* Focus trap — only while the overlay is up. */
  document.addEventListener('keydown', function (event) {
    if (!isOpen || event.key !== 'Tab') return;

    var items = Array.prototype.slice
      .call(header.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.offsetParent !== null;
      });

    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* A sticky header that simply sits there eats vertical space on a long
     editorial page. This gives the space back while reading and returns the
     header the moment the reader looks for it — scrolling up. Nothing moves
     while the mobile overlay is open, and nothing moves at the very top. */
  if (header.classList.contains('hdr--sticky')) {
    var lastY = window.scrollY;
    var ticking = false;

    var onScroll = function () {
      var y = window.scrollY;
      var threshold = header.offsetHeight * 2;

      if (isOpen || y < threshold) {
        header.removeAttribute('data-hidden');
      } else if (y > lastY + 4) {
        header.setAttribute('data-hidden', '');
      } else if (y < lastY - 4) {
        header.removeAttribute('data-hidden');
      }

      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(onScroll);
    }, { passive: true });

    /* Never leave a keyboard user tabbing into a header that is off screen. */
    header.addEventListener('focusin', function () {
      header.removeAttribute('data-hidden');
    });
  }

  /* Crossing the breakpoint while the overlay is open would otherwise leave the
     body scroll-locked with no visible way to unlock it. */
  function handleBreakpoint(event) {
    if (event.matches && isOpen) closeNav(false);
  }

  if (DESKTOP.addEventListener) {
    DESKTOP.addEventListener('change', handleBreakpoint);
  } else if (DESKTOP.addListener) {
    DESKTOP.addListener(handleBreakpoint);
  }
})();
